/**
 * ChatPage.tsx
 *
 * Page component for chatbot interactions and message rendering.
 */

import React, { useEffect, lazy, Suspense, useCallback } from "react";
import VirtualizedList from '../../components/VirtualizedList';
import { FiSearch, FiSend, FiMenu, FiX } from "react-icons/fi";
import { useChatViewModel } from "../../viewmodel/chatBot/ChatViewModel";
import "./ChatPage.css";

// Lazy load heavy components for better performance
const MessageRenderer = lazy(() => import("../../components/MessageRenderer"));
const LoadingScreen = lazy(() => import("../../components/LoadingScreen"));

// Optimize loading messages - moved outside component to prevent recreation
const LOADING_MESSAGES = [
    "Fetching your data.",
    "Fetching your data..",
    "Fetching your data...",
    "Please wait.",
    "Please wait..",
    "Please wait...",
    "Almost done.",
    "Almost done..",
    "Almost done..."
];

export default function ChatPage() {
    const vm = useChatViewModel();

    // Memoized render row function to prevent unnecessary re-renders
    const renderMessageRow = useCallback((msg: any, i: number) => {
        const isUser = msg.sender?.toLowerCase() === "user";
        const messageKey = msg.id || `msg_${i}`;
        const animationState = vm.messageAnimations?.[messageKey];
        if (msg.type === "loading") {
            return (
                <div key={messageKey} className="chat-message bot loading">
                    <div className="message-avatar">
                        <img 
                            src="https://cdn-icons-png.flaticon.com/512/4712/4712027.png" 
                            alt="AI Assistant"
                            loading="lazy"
                        />
                    </div>
                    <div className="message-content">
                        <div className="typing-indicator">
                            <span></span>
                            <span></span>
                            <span></span>
                        </div>
                        <span className="loading-text">{LOADING_MESSAGES[vm.loadingMsgIdx]}</span>
                    </div>
                </div>
            );
        }
        return (
            <div 
                key={messageKey} 
                className={`chat-message ${isUser ? "user" : "bot"} ${animationState?.isEntering ? 'entering' : 'entered'}`}
                style={{
                    opacity: animationState?.opacity ?? 1,
                    transform: animationState?.transform ?? 'translateX(0) scale(1)',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
                }}
            >
                {!isUser && (
                    <div className="message-avatar">
                        <img 
                            src="https://cdn-icons-png.flaticon.com/512/4712/4712027.png" 
                            alt="AI Assistant"
                            loading="lazy"
                            onLoad={() => vm.lazyLoadImage?.("https://cdn-icons-png.flaticon.com/512/4712/4712027.png")}
                        />
                    </div>
                )}
                <div className="message-content">
                    <Suspense fallback={<></>}>
                        <MessageRenderer text={msg.text} />
                    </Suspense>
                    {!isUser && msg.id && msg.type !== "loading" && !msg.text.includes("Hi, Ask me anything") && (
                        <div className="message-feedback">
                            <div className={`feedback-text ${msg.feedback !== undefined && msg.feedback !== null ? 'thankyou' : ''}`}>
                                {msg.feedback !== undefined && msg.feedback !== null ? 
                                    `Thank you for your feedback! ${msg.feedback === 1 ? '👍' : '👎'}` : 
                                    'Please share your response:'
                                }
                            </div>
                            <div className="feedback-buttons">
                                <button 
                                    className={`feedback-btn thumbs-up ${msg.feedback === 1 ? 'active' : ''} ${vm.feedbackLoading[msg.id!] ? 'loading' : ''}`}
                                    onClick={() => vm.handleMessageFeedback(msg.id!, 1)}
                                    disabled={vm.feedbackLoading[msg.id!] || (msg.feedback !== undefined && msg.feedback !== null)}
                                    title={`Thumbs up ${msg.feedback === 1 ? '(selected)' : ''}`}
                                >
                                    {vm.feedbackLoading[msg.id!] && msg.feedback === 1 ? (
                                        <span className="feedback-spinner">⏳</span>
                                    ) : (
                                        '👍'
                                    )}
                                </button>
                                <button 
                                    className={`feedback-btn thumbs-down ${msg.feedback === 0 ? 'active' : ''} ${vm.feedbackLoading[msg.id!] ? 'loading' : ''}`}
                                    onClick={() => vm.handleMessageFeedback(msg.id!, 0)}
                                    disabled={vm.feedbackLoading[msg.id!] || (msg.feedback !== undefined && msg.feedback !== null)}
                                    title={`Thumbs down ${msg.feedback === 0 ? '(selected)' : ''}`}
                                >
                                    {vm.feedbackLoading[msg.id!] && msg.feedback === 0 ? (
                                        <span className="feedback-spinner">⏳</span>
                                    ) : (
                                        '👎'
                                    )}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                {isUser && (
                    <div className="message-avatar">
                        <img 
                            src="https://www.w3schools.com/howto/img_avatar.png" 
                            alt={vm.currentUser?.name || 'User'}
                            loading="lazy"
                            onLoad={() => vm.lazyLoadImage?.("https://www.w3schools.com/howto/img_avatar.png")}
                        />
                    </div>
                )}
            </div>
        );
    }, [vm.messageAnimations, vm.loadingMsgIdx, vm.feedbackLoading, vm.handleMessageFeedback, vm.lazyLoadImage, vm.currentUser?.name]);

    // Handle mobile viewport and keyboard issues
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth <= 900) {
                const vh = window.innerHeight * 0.01;
                document.documentElement.style.setProperty('--vh', `${vh}px`);
            }
        };

        const handleScroll = () => {
            if (window.innerWidth <= 900) {
                const inputBar = document.querySelector('.chat-input-bar') as HTMLElement | null;
                if (inputBar) {
                    inputBar.style.position = 'fixed';
                    inputBar.style.bottom = '0px';
                    inputBar.style.zIndex = '9999';
                    inputBar.style.transform = 'translateZ(0)';
                }
            }
        };

        const preventBounce = (e: TouchEvent) => {
            const target = e.target as HTMLElement;
            const chatMessages = target.closest('.chat-messages');
            if (chatMessages) {
                const { scrollTop, scrollHeight, clientHeight } = chatMessages;
                
                if (scrollTop === 0 && e.touches[0].clientY > e.touches[0].clientY) {
                    e.preventDefault();
                }
                
                if (scrollTop + clientHeight >= scrollHeight && e.touches[0].clientY < e.touches[0].clientY) {
                    e.preventDefault();
                }
            }
        };

        handleResize();
        window.addEventListener('resize', handleResize);
        window.addEventListener('orientationchange', handleResize);
        window.addEventListener('scroll', handleScroll, { passive: true });
        document.addEventListener('touchmove', preventBounce, { passive: false });

        return () => {
            window.removeEventListener('resize', handleResize);
            window.removeEventListener('orientationchange', handleResize);
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('touchmove', preventBounce);
        };
    }, []);

    // Show loading screen only for initial app loading
    if (vm.initialLoading) {
        return (
            <Suspense fallback={<div></div>}>
                <LoadingScreen message="Loading your chat..." />
            </Suspense>
        );
    }

    return (
        <div className="chat-root">
            {/* Mobile sidebar overlay */}
            {vm.sidebarOpen && (
                <div 
                    className="sidebar-overlay visible"
                    onClick={() => vm.setSidebarOpen(false)}
                    aria-label="Close sidebar"
                />
            )}
            
            {/* Mobile menu icon */}
            <button
                className="mobile-sidebar-toggle"
                onClick={() => vm.setSidebarOpen(!vm.sidebarOpen)}
                aria-label="Open chat history"
            >
                {vm.sidebarOpen ? <FiX /> : <FiMenu />}
            </button>
            {/* SIDEBAR */}
            <aside className={`chat-sidebar${vm.sidebarOpen ? " open" : ""}`}>
                <div className="sidebar-content">
                    <div className="sidebar-header">HMG Business</div>
                    <div className="sidebar-tools">
                        <button className="new-chat-btn" onClick={vm.handleRefresh}>New Chat ✏️</button>
                        <div className="sidebar-search">
                            <FiSearch />
                            <input
                                placeholder="Search Chat"
                                value={vm.chatSearch}
                                onChange={e => vm.setChatSearch(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="sidebar-history">
                        <div className="sidebar-history-title">Chat History</div>
                        <nav>
                            {vm.filteredChats.length === 0 && (
                                <span style={{ color: "#aaa" }}>No chats found.</span>
                            )}
                            {vm.filteredChats.map((chat, index) => (
                                <button
                                    key={chat.id}
                                    className={`chat-history-btn${vm.selectedChatId === chat.id ? " selected" : ""}`}
                                    onClick={() => vm.handleSelectChat(chat.id)}
                                    style={{
                                        animationName: 'slideInLeft',
                                        animationDuration: '0.3s',
                                        animationTimingFunction: 'ease-out',
                                        animationFillMode: 'forwards',
                                        animationDelay: `${index * 50}ms`
                                    }}
                                >
                                    <div className="chat-title">{chat.title}</div>
                                    <div className="chat-preview">
                                        <span className="message-count">
                                            {vm.selectedChatId === chat.id ? 
                                                `${vm.currentChatQuestionCount} questions asked` : 
                                                'Click to view messages'
                                            }
                                        </span>
                                        <span className="chat-status">
                                            {vm.selectedChatId === chat.id ? 'Current chat' : 'Tap to open'}
                                        </span>
                                    </div>
                                </button>
                            ))}
                        </nav>
                    </div>
                </div>
                <div className="sidebar-user">
                    <img src='https://www.w3schools.com/howto/img_avatar.png' alt={vm.currentUser?.name || 'Guest User'} loading="lazy" />
                    <div>{vm.currentUser?.name || 'Guest User'}</div>
                </div>
            </aside>
            {/* CHAT BODY */}
            <main className="chat-main">
                <div className="chat-messages">
                    {/* Load more indicator for lazy loading - show at top */}
                    {vm.hasMoreMessages && vm.isUIStable && !vm.loadingMessages && (
                        <div ref={vm.loadMoreRef} className="load-more-indicator">
                            <div className="load-more-spinner"></div>
                            <span>Loading older messages...</span>
                        </div>
                    )}
                    
                    {/* Show all messages immediately - no chunking */}
                    {vm.isUIStable && vm.messages && vm.messages.length > 0 && (
                        <VirtualizedList
                            data={vm.messages}
                            width={'100%'}
                            height={'100%'}
                            renderRow={renderMessageRow}
                            getKey={(msg:any, i) => msg.id || `msg_${i}`}
                            followOutput
                        />
                    )}
                    
                    <div ref={vm.messagesEndRef} />
                </div>
                {/* Chat Input */}
                <div className={`chat-input-bar ${vm.sendingMessage ? 'sending' : ''}`}>
                    <div className="input-container">
                        <input
                            value={vm.input}
                            onChange={e => vm.setInput(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && !e.shiftKey && vm.handleSend()}
                            placeholder="Ask me about HMG"
                            disabled={vm.loading || vm.loadingMessages}
                            className={vm.isTyping ? 'typing' : ''}
                        />
                        {vm.isTyping && (
                            <div className="typing-indicator-small">
                                <span></span>
                                <span></span>
                                <span></span>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={vm.handleSend}
                        disabled={vm.loading || !vm.input.trim()}
                        className={`chat-send-btn ${vm.sendingMessage ? 'sending' : ''}`}
                    >
                        {vm.sendingMessage ? (
                            <div className="send-spinner"></div>
                        ) : (
                            <FiSend />
                        )}
                    </button>
                </div>
            </main>

            {/* Detailed Feedback Popup */}
            {vm.showFeedbackPopup && (
                <div className="feedback-popup-overlay">
                    <div className="feedback-popup">
                        <div className="feedback-popup-header">
                            <h3>We value your feedback</h3>
                            <button 
                                className="feedback-popup-close"
                                onClick={vm.handleCloseFeedbackPopup}
                                disabled={vm.sendingDetailedFeedback}
                            >
                                <FiX />
                            </button>
                        </div>
                        <div className="feedback-popup-content">
                            <p>We noticed that you are not satisfied with the response of Neuron AI. Please share your feedback regarding the same.</p>
                            <textarea
                                className="feedback-popup-textarea"
                                placeholder="Please describe what went wrong or how we can improve..."
                                value={vm.detailedFeedback}
                                onChange={(e) => vm.setDetailedFeedback(e.target.value)}
                                disabled={vm.sendingDetailedFeedback}
                                rows={4}
                            />
                        </div>
                        <div className="feedback-popup-actions">
                            <button 
                                className="feedback-popup-btn feedback-popup-btn-secondary"
                                onClick={vm.handleCloseFeedbackPopup}
                                disabled={vm.sendingDetailedFeedback}
                            >
                                Skip
                            </button>
                            <button 
                                className="feedback-popup-btn feedback-popup-btn-primary"
                                onClick={vm.handleDetailedFeedback}
                                disabled={vm.sendingDetailedFeedback || !vm.detailedFeedback.trim()}
                            >
                                {vm.sendingDetailedFeedback ? (
                                    <>
                                        <div className="feedback-popup-spinner"></div>
                                        Sending...
                                    </>
                                ) : (
                                    'Submit Feedback'
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}